import { getItemEconomics, offerRuleOn } from './evaluateBuyingRules.js';

// Matrix economics on the item card (spec 20260713): GMROI at the matched
// cell's grain (family average only on the default cell, labelled as such),
// the item's BP/SP against its condition-adjusted cell target with
// stance framing, a visible fallback notice when the sub missed the matrix
// (Guard 2 — never a silent main-as-sub match).
// The full rationale renders in the always-visible MatrixWhyLine below the row.
// Pure derivation from current page state — re-renders live on every edit.
//
// With the offer rule on, nothing renders: the offer is the rule's figure, so a
// target to compare it with, its fallback notice and the GMROI behind that target
// say nothing the Price breakdown does not. The consign/outright chip was removed
// 2026-10-23; the path still shows in the Why popover.
const STANCE_OP = { push: '≥', careful: '≈', cut: '≤' };
const STANCE_WORD = { push: 'floor', careful: 'target', cut: 'cap' };

export default function ItemEconomicsBadges({ item, mainCategory, subCategory, config }) {
    if (!config || offerRuleOn(config)) return null;
    const e = getItemEconomics(item, mainCategory, config, subCategory);
    const warned = (e.stance !== 'cut' && e.underTarget) || (e.stance !== 'push' && e.overTarget);
    const tone = warned ? (e.underTarget ? 'qa-econ-under' : 'qa-econ-over') : 'badge-neutral';
    const fallbackNote = e.matchLevel === 'sub' ? ''
        : e.matchLevel === 'main' ? ' — using main default (sub unrecognised)'
        : subCategory ? ' — using matrix default (sub not in matrix)' : ' — using matrix default (no sub set)';
    const conditionNote = e.conditionKnown ? '' : ' — condition unknown, no modifier applied';
    return (
        <>
            {e.gmroi != null && (
                <span className="badge badge-sm badge-neutral qa-econ"
                    title={e.gmroiGrain === 'family'
                        ? 'Main-category GMROI (family average) — no matrix cell for this sub'
                        : `Sub-category GMROI — capital-review scorecard${e.dioDays != null ? ` · sells in ~${e.dioDays} days` : ''}`}>
                    GMROI {e.gmroi}{e.gmroiGrain === 'family' ? ' (family avg)' : ''}
                </span>
            )}
            <span className={`badge badge-sm qa-econ ${tone}`}
                title={`BP/SP vs the ${e.stance} ${STANCE_WORD[e.stance]} for this sub × price band${fallbackNote}${conditionNote}`}>
                {e.stance} · BP {e.currentRatio == null ? '—' : pct(e.currentRatio)} / {STANCE_OP[e.stance]}{e.targetRatio == null ? '—' : pct(e.targetRatio)}
            </span>
            {e.matchLevel !== 'sub' && (
                <span className="badge badge-sm qa-econ qa-econ-fallback"
                    title={`No matrix cell matched this item's sub-category${fallbackNote}. Set the sub to price against its own ladder.`}>
                    {e.matchLevel === 'main' ? 'main default' : 'matrix default'}
                </span>
            )}
        </>
    );
}

const pct = (ratio) => `${Math.round(ratio * 1000) / 10}%`;
